import type {
  ProfileFormData,
  ProfileChanges,
  OriginalProfileData,
} from "../schemas/profile.schema";

/**
 * Utility to detect changes in profile data
 * Following single responsibility principle - this only handles change detection
 */
export class ProfileChangeDetector {
  private originalData: OriginalProfileData;

  constructor(originalData: OriginalProfileData) {
    this.originalData = originalData;
  }

  /**
   * Analyzes current form data against original data
   * Returns comprehensive change information
   */
  detectChanges(currentData: ProfileFormData): ProfileChanges {
    const changedFields: Array<keyof ProfileFormData> = [];

    if (currentData.firstName !== this.originalData.firstName) {
      changedFields.push("firstName");
    }

    if (currentData.lastName !== this.originalData.lastName) {
      changedFields.push("lastName");
    }

    if (currentData.email !== this.originalData.email) {
      changedFields.push("email");
    }

    const hasNameChanges =
      changedFields.includes("firstName") || changedFields.includes("lastName");
    const hasEmailChanges = changedFields.includes("email");
    const hasAnyChanges = changedFields.length > 0;

    return {
      hasNameChanges,
      hasEmailChanges,
      hasAnyChanges,
      changedFields,
    };
  }

  /**
   * Updates the original data reference (e.g., after successful save)
   */
  updateOriginalData(newOriginalData: OriginalProfileData): void {
    this.originalData = newOriginalData;
  }

  /**
   * Gets the current original data
   */
  getOriginalData(): OriginalProfileData {
    return { ...this.originalData };
  }
}
